import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Plus, AlertTriangle, Trash2, Edit } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useAdminEmailFlowQuery,
  useAdminEmailFlowTriggersQuery,
  useAdminCreateEmailFlowMutation,
  useAdminUpdateEmailFlowMutation,
  useAdminDeleteEmailFlowMutation,
  useAdminDeleteEmailFlowStepMutation,
  useAdminReorderEmailFlowStepsMutation,
} from '@/generated/graphql';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import StepEditorDialog from './_emailFlows/StepEditorDialog';
import RunsCard from './_emailFlows/RunsCard';

type StepLite = {
  id: string;
  order: number;
  delay_minutes: number;
  subject_pt: string;
  body_pt: string;
  subject_en?: string | null;
  body_en?: string | null;
  is_active: boolean;
};

const formatDelay = (m: number) => {
  if (!m) return 'immediately';
  if (m < 60) return `${m} min`;
  if (m < 1440) return `${Math.round(m / 60)} h`;
  return `${Math.round(m / 1440)} d`;
};

const AdminEmailFlowDetail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id;

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [trigger, setTrigger] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [isTransactional, setIsTransactional] = useState(false);
  const [isEditable, setIsEditable] = useState(true);
  const [audience, setAudience] = useState<'all' | 'own_brand' | 'other_channels'>('all');

  const [editingStep, setEditingStep] = useState<StepLite | null>(null);
  const [stepDialogOpen, setStepDialogOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const { data: triggersData } = useAdminEmailFlowTriggersQuery();
  const { data, refetch } = useAdminEmailFlowQuery({
    variables: { id: id! },
    skip: isNew,
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (!data?.adminEmailFlow) return;
    const f = data.adminEmailFlow;
    setName(f.name);
    setSlug(f.slug);
    setDescription(f.description || '');
    setTrigger(f.trigger);
    setIsActive(f.is_active);
    setIsTransactional(f.is_transactional);
    setIsEditable(f.is_editable);
    setAudience((f.audience as 'all' | 'own_brand' | 'other_channels') || 'all');
  }, [data]);

  const [createFlow, { loading: creating }] = useAdminCreateEmailFlowMutation();
  const [updateFlow, { loading: updating }] = useAdminUpdateEmailFlowMutation();
  const [deleteFlow] = useAdminDeleteEmailFlowMutation();
  const [deleteStep] = useAdminDeleteEmailFlowStepMutation();
  const [reorderSteps] = useAdminReorderEmailFlowStepsMutation();

  const triggers = triggersData?.adminEmailFlowTriggers ?? [];
  const flow = data?.adminEmailFlow;
  const steps: StepLite[] = (flow?.steps ?? []) as StepLite[];

  const handleSaveSettings = async () => {
    try {
      if (isNew) {
        const res = await createFlow({ variables: { input: { name, description, trigger, audience, is_active: isActive, is_transactional: isTransactional, is_editable: isEditable } } });
        const newId = res.data?.adminCreateEmailFlow?.id;
        if (newId) navigate(`/admin/email-flows/${newId}`);
        toast.success('Saved');
      } else {
        await updateFlow({ variables: { id: id!, input: { name, description, trigger, audience, is_active: isActive, is_transactional: isTransactional, is_editable: isEditable } } });
        await refetch();
        toast.success('Saved');
      }
    } catch (e: any) {
      toast.error(e.message || 'Save failed');
    }
  };

  const handleDeleteFlow = async () => {
    if (!id) return;
    setConfirmDeleteOpen(false);
    try {
      await deleteFlow({ variables: { id } });
      toast.success(t('admin.emailFlows.delete.success'));
      navigate('/admin/email-flows');
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const handleDeleteStep = async (stepId: string) => {
    try {
      await deleteStep({ variables: { id: stepId } });
      await refetch();
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const moveStep = async (idx: number, dir: -1 | 1) => {
    const newOrder = [...steps];
    const target = idx + dir;
    if (target < 0 || target >= newOrder.length) return;
    [newOrder[idx], newOrder[target]] = [newOrder[target], newOrder[idx]];
    try {
      await reorderSteps({ variables: { flowId: id!, stepIds: newOrder.map((s) => s.id) } });
      await refetch();
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const variablesForTrigger = triggers.find((tr) => tr.key === trigger)?.variables ?? [];

  return (
    <Layout>
      <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
        <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/email-flows')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-semibold">{isNew ? t('admin.emailFlows.new') : flow?.name}</h1>
      </div>

      <Card>
        <CardHeader><CardTitle>Settings</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('admin.emailFlows.fields.name')}</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <Label>{t('admin.emailFlows.fields.slug')}</Label>
              <Input value={slug} disabled placeholder="auto-generated" />
            </div>
          </div>
          <div>
            <Label>{t('admin.emailFlows.fields.description')}</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 items-end">
            <div>
              <Label>{t('admin.emailFlows.fields.trigger')}</Label>
              <Select value={trigger} onValueChange={setTrigger}>
                <SelectTrigger><SelectValue placeholder="Select trigger" /></SelectTrigger>
                <SelectContent>
                  {triggers.map((tr) => (
                    <SelectItem key={tr.key} value={tr.key}>{tr.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('admin.emailFlows.fields.audience')}</Label>
              <Select value={audience} onValueChange={(v) => setAudience(v as 'all' | 'own_brand' | 'other_channels')}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('admin.emailFlows.audience.all')}</SelectItem>
                  <SelectItem value="own_brand">{t('admin.emailFlows.audience.ownBrand')}</SelectItem>
                  <SelectItem value="other_channels">{t('admin.emailFlows.audience.otherChannels')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={isActive} onCheckedChange={setIsActive} />
              <Label>{t('admin.emailFlows.fields.isActive')}</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={isTransactional} onCheckedChange={setIsTransactional} />
              <Label>{t('admin.emailFlows.fields.isTransactional')}</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={isEditable} onCheckedChange={setIsEditable} />
              <Label>{t('admin.emailFlows.fields.isEditable')}</Label>
            </div>
          </div>
          {!isNew && !isEditable && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{t('admin.emailFlows.managedBySeedWarning')}</AlertDescription>
            </Alert>
          )}
          <div className="flex justify-between">
            <Button onClick={handleSaveSettings} disabled={creating || updating}>Save</Button>
            {!isNew && <Button variant="destructive" onClick={() => setConfirmDeleteOpen(true)}><Trash2 className="h-4 w-4 mr-2" />Delete flow</Button>}
          </div>
        </CardContent>
      </Card>

      {!isNew && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>{t('admin.emailFlows.steps.title')}</CardTitle>
              <Button onClick={() => { setEditingStep(null); setStepDialogOpen(true); }}>
                <Plus className="h-4 w-4 mr-2" /> {t('admin.emailFlows.steps.add')}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Alert className="mb-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{t('admin.emailFlows.inFlightWarning')}</AlertDescription>
            </Alert>
            <div className="space-y-2">
              {steps.map((s, idx) => (
                <div key={s.id} className="flex items-center gap-3 border rounded p-3">
                  <div className="flex flex-col">
                    <Button size="icon" variant="ghost" disabled={idx === 0} onClick={() => moveStep(idx, -1)}>↑</Button>
                    <Button size="icon" variant="ghost" disabled={idx === steps.length - 1} onClick={() => moveStep(idx, 1)}>↓</Button>
                  </div>
                  <div className="flex-1">
                    <div className="text-sm text-muted-foreground">{formatDelay(s.delay_minutes)}</div>
                    <div className="font-medium">{s.subject_pt}</div>
                  </div>
                  <Switch checked={s.is_active} disabled />
                  <Button variant="outline" size="sm" onClick={() => { setEditingStep(s); setStepDialogOpen(true); }}>
                    <Edit className="h-4 w-4 mr-1" /> {t('admin.emailFlows.steps.edit')}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDeleteStep(s.id)}>
                    <Trash2 className="h-4 w-4 mr-1" /> {t('admin.emailFlows.steps.delete')}
                  </Button>
                </div>
              ))}
              {steps.length === 0 && <div className="text-sm text-muted-foreground">—</div>}
            </div>
          </CardContent>
        </Card>
      )}

      {!isNew && (
        <RunsCard flowId={id!} />
      )}

      {!isNew && id && (
        <StepEditorDialog
          open={stepDialogOpen}
          onOpenChange={setStepDialogOpen}
          flowId={id}
          step={editingStep}
          nextOrder={(steps.length > 0 ? Math.max(...steps.map((s) => s.order)) : 0) + 1}
          variables={variablesForTrigger}
          onSaved={() => refetch()}
        />
      )}

      <AlertDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin.emailFlows.delete.confirm')}</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteFlow}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
        </div>
      </main>
    </Layout>
  );
};

export default AdminEmailFlowDetail;
